import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Dog, Building2, Package, DollarSign, Trash2, Plus, ClipboardList, BellRing, Megaphone, Ticket, Send } from "lucide-react";
import { toast } from "sonner";
import { Layout, PageHeader } from "../components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const IN = "w-full bg-eon-bg border border-eon-border px-3 py-2.5 text-sm focus:border-eon-gold outline-none";
const EmptyRow = ({ text }) => <div className="text-center py-16 text-eon-muted border border-eon-border">{text}</div>;

const WL_LABEL = {
  puppy_waitlist: "Puppies", academy_waitlist: "Academy (all)", academy_track_waitlist: "Academy Track",
  course_waitlist: "Course", digital_waitlist: "Digital Product", product_waitlist: "Nutrition / Product",
};

function BlastDialog({ group, promos, onSend }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [busy, setBusy] = useState(false);
  const label = `${WL_LABEL[group.subject_type] || group.subject_type} · ${group.subject_id}`;
  const send = async () => {
    if (!message.trim()) { toast.error("Write a message first"); return; }
    setBusy(true);
    await onSend({ subject_type: group.subject_type, subject_id: group.subject_id, subject_label: WL_LABEL[group.subject_type] || group.subject_type, message, promo_code: promoCode || undefined });
    setBusy(false); setOpen(false); setMessage(""); setPromoCode("");
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`blast-open-${group.subject_type}-${group.subject_id}`} className="flex items-center gap-2 bg-eon-gold text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 hover:bg-eon-goldhover transition-colors disabled:opacity-40" disabled={group.count === 0}>
          <Send size={14} /> Send Blast
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white border-eon-border text-eon-text">
        <DialogHeader><DialogTitle className="font-display text-2xl">Email the {label} waitlist</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <p className="text-sm text-eon-muted">{group.count} {group.count === 1 ? "person" : "people"} will receive this email.</p>
          <textarea rows={5} placeholder="Write your announcement… e.g. Great news — enrollment opens Monday!" value={message} onChange={(e) => setMessage(e.target.value)} className={IN} data-testid="blast-message" />
          <div>
            <div className="eon-label mb-1">Attach an early-bird promo code (optional)</div>
            <select value={promoCode} onChange={(e) => setPromoCode(e.target.value)} className={IN} data-testid="blast-promo-select">
              <option value="">None</option>
              {promos.filter((p) => p.active).map((p) => <option key={p.id} value={p.code}>{p.code} — {p.percent_off}% off</option>)}
            </select>
          </div>
          <button onClick={send} disabled={busy} className="w-full bg-eon-gold text-white font-bold uppercase tracking-wider py-3 hover:bg-eon-goldhover transition-colors disabled:opacity-40" data-testid="blast-send">
            {busy ? "Sending…" : `Send to ${group.count}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WaitlistsTab({ waitlists, promos, onSend }) {
  return (
    <TabsContent value="waitlists" className="pt-8 space-y-3">
      <p className="text-sm text-eon-muted mb-2">Everyone who signed up to be notified about a Coming Soon item (puppies, academy, courses, digital products, nutrition). Write a message and send a blast — attach a promo code to reward early sign-ups. <span className="text-eon-text font-semibold">Emails send once your Resend key is added.</span></p>
      {waitlists.map((g) => (
        <div key={`${g.subject_type}-${g.subject_id}`} className="eon-card p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-waitlist-${g.subject_type}-${g.subject_id}`}>
          <Megaphone size={18} className="text-eon-gold shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold">{WL_LABEL[g.subject_type] || g.subject_type} <span className="text-eon-muted font-normal">· {g.subject_id}</span></div>
            <div className="text-xs text-eon-muted mt-1">{g.count} {g.count === 1 ? "subscriber" : "subscribers"}</div>
          </div>
          <BlastDialog group={g} promos={promos} onSend={onSend} />
        </div>
      ))}
      {waitlists.length === 0 && <EmptyRow text="No waitlist sign-ups yet." />}
    </TabsContent>
  );
}

function PromoCodesTab({ promos, onCreate, onToggle, onDelete }) {
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(20);
  const [maxRed, setMaxRed] = useState(0);
  const [expires, setExpires] = useState("");
  const create = async () => {
    if (!code.trim()) { toast.error("Enter a code"); return; }
    await onCreate({ code: code.trim().toUpperCase(), percent_off: Number(percent), max_redemptions: Number(maxRed) || 0, expires_at: expires || undefined });
    setCode(""); setPercent(20); setMaxRed(0); setExpires("");
  };
  return (
    <TabsContent value="promos" className="pt-8 space-y-4">
      <p className="text-sm text-eon-muted">Create discount codes buyers enter at checkout (memberships & digital products). Attach them to a waitlist blast to reward early sign-ups.</p>
      <div className="eon-card p-5 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" data-testid="promo-create-row">
        <div><div className="eon-label mb-1">Code</div><input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="EARLYBIRD30" className={IN} data-testid="promo-code-input" /></div>
        <div><div className="eon-label mb-1">% Off</div><input type="number" min="1" max="100" value={percent} onChange={(e) => setPercent(e.target.value)} className={IN} data-testid="promo-percent-input" /></div>
        <div><div className="eon-label mb-1">Max uses (0=∞)</div><input type="number" min="0" value={maxRed} onChange={(e) => setMaxRed(e.target.value)} className={IN} /></div>
        <div><div className="eon-label mb-1">Expires (optional)</div><input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className={IN} /></div>
        <button onClick={create} className="flex items-center justify-center gap-2 bg-eon-gold text-white text-xs font-bold uppercase tracking-wider px-4 py-3 hover:bg-eon-goldhover transition-colors" data-testid="promo-create-btn"><Plus size={16} /> Create</button>
      </div>
      <div className="space-y-3">
        {promos.map((p) => (
          <div key={p.id} className="eon-card p-5 flex items-center gap-4" data-testid={`admin-promo-${p.id}`}>
            <Ticket size={18} className="text-eon-gold shrink-0" />
            <div className="flex-1">
              <div className="font-display font-bold tracking-wide">{p.code} <span className="text-eon-gold">— {p.percent_off}% off</span></div>
              <div className="text-xs text-eon-muted mt-1">{p.uses} used{p.max_redemptions ? ` / ${p.max_redemptions} max` : ""}{p.expires_at ? ` · expires ${String(p.expires_at).slice(0, 10)}` : ""} · {p.active ? "Active" : "Disabled"}</div>
            </div>
            <button onClick={() => onToggle(p.id, !p.active)} data-testid={`promo-toggle-${p.id}`} className={`text-xs font-bold uppercase tracking-wider px-4 py-2 ${p.active ? "border border-eon-border text-eon-muted" : "bg-eon-gold text-white"}`}>{p.active ? "Disable" : "Enable"}</button>
            <button onClick={() => onDelete(p.id)} data-testid={`promo-delete-${p.id}`} className="text-eon-muted hover:text-red-500 p-2"><Trash2 size={18} /></button>
          </div>
        ))}
        {promos.length === 0 && <EmptyRow text="No promo codes yet." />}
      </div>
    </TabsContent>
  );
}

function ApplicationsTab({ applications, updateApp }) {
  return (
    <TabsContent value="applications" className="pt-8 space-y-3">
      {applications.map((a) => (
        <div key={a.id} className="eon-card p-5" data-testid={`admin-application-${a.id}`}>
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="font-display font-bold text-lg">{a.dog_name}</div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${a.status === "approved" || a.status === "reserved" ? "bg-green-500/15 text-green-400" : a.status === "declined" ? "bg-red-500/15 text-red-400" : "bg-yellow-500/15 text-yellow-400"}`}>{a.status}</span>
              </div>
              <div className="text-sm mt-2">{a.full_name} · {a.email} {a.phone && `· ${a.phone}`}</div>
              <div className="text-xs text-eon-muted mt-1">{a.city}{a.state && `, ${a.state}`} · {a.home_type}{a.has_yard ? " · has yard" : ""} · {a.experience} · purpose: {a.purpose} · timeline: {a.timeline}</div>
              {a.household && <p className="text-xs text-eon-text/70 mt-2"><span className="text-eon-muted">Household:</span> {a.household}</p>}
              {a.why_boerboel && <p className="text-xs text-eon-text/70 mt-1"><span className="text-eon-muted">Motivation:</span> {a.why_boerboel}</p>}
            </div>
            <div className="flex flex-col gap-2 md:w-48 shrink-0">
              <select value={a.status} onChange={(e) => updateApp(a.id, { status: e.target.value })} data-testid={`app-status-select-${a.id}`} className="bg-eon-bg border border-eon-border px-3 py-2 text-sm">
                {["pending", "approved", "waitlisted", "declined"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input defaultValue={a.admin_note} placeholder="Note to applicant" onBlur={(e) => { if (e.target.value !== (a.admin_note || "")) updateApp(a.id, { admin_note: e.target.value }); }} data-testid={`app-note-${a.id}`} className="bg-eon-bg border border-eon-border px-3 py-2 text-xs" />
            </div>
          </div>
        </div>
      ))}
      {applications.length === 0 && <EmptyRow text="No applications yet." />}
    </TabsContent>
  );
}

function NotifyTab({ notify, sendNotify }) {
  return (
    <TabsContent value="notify" className="pt-8 space-y-3">
      <p className="text-sm text-eon-muted mb-2">Waitlist (Coming Soon puppies) and restock (Sold Out / Coming Soon products) subscribers. Alerts auto-send when you flip an item to available; use "Send Alerts" to notify manually.</p>
      {notify.map((n) => (
        <div key={`${n.kind}-${n.item_id}`} className="eon-card p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-notify-${n.item_id}`}>
          <BellRing size={18} className="text-eon-gold shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold">{n.item_name}</div>
            <div className="text-xs text-eon-muted mt-1">{n.kind === "dog" ? "Puppy waitlist" : "Product restock"} · {n.pending} pending · {n.notified} notified</div>
          </div>
          <button onClick={() => sendNotify(n.kind, n.item_id)} disabled={n.pending === 0} data-testid={`notify-send-${n.item_id}`}
            className="text-xs font-bold uppercase tracking-wider px-4 py-2.5 bg-eon-gold text-white hover:bg-eon-goldhover transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Send Alerts</button>
        </div>
      ))}
      {notify.length === 0 && <EmptyRow text="No waitlist or restock subscribers yet." />}
    </TabsContent>
  );
}

function BreedersTab({ breeders, updateBreeder }) {
  return (
    <TabsContent value="breeders" className="pt-8 space-y-3">
      {breeders.map((b) => (
        <div key={b.id} className="eon-card p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-breeder-${b.id}`}>
          <img src={b.image} alt="" className="w-14 h-14 object-cover" />
          <div className="flex-1"><div className="font-display font-bold">{b.business_name}</div><div className="text-xs text-eon-muted">{b.location} · {b.approved ? "Approved" : "Pending"}</div></div>
          <select value={b.tier} onChange={(e) => updateBreeder(b.id, { tier: e.target.value })} data-testid={`breeder-tier-${b.id}`} className="bg-eon-bg border border-eon-border px-3 py-2 text-sm">
            {["free", "verified", "pro", "featured"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={() => updateBreeder(b.id, { approved: !b.approved })} data-testid={`breeder-approve-${b.id}`}
            className={`text-xs font-bold uppercase tracking-wider px-4 py-2 ${b.approved ? "border border-eon-border text-eon-muted" : "bg-eon-gold text-white"}`}>{b.approved ? "Suspend" : "Approve"}</button>
        </div>
      ))}
    </TabsContent>
  );
}

function DogsTab({ dogs, updateDog, delDog, loadAll }) {
  return (
    <TabsContent value="dogs" className="pt-8">
      <div className="flex justify-end mb-4"><NewDogDialog onDone={loadAll} /></div>
      <div className="space-y-3">
        {dogs.map((d) => (
          <div key={d.id} className="eon-card p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-dog-${d.id}`}>
            <img src={d.image} alt="" className="w-14 h-14 object-cover" />
            <div className="flex-1"><div className="font-display font-bold">{d.name} {d.is_boerboel && <span className="eon-label">E.O.N</span>}</div><div className="text-xs text-eon-muted">{d.breed} · ${d.price?.toLocaleString()}</div></div>
            <select value={d.status} onChange={(e) => updateDog(d.id, { status: e.target.value })} data-testid={`dog-status-${d.id}`} className="bg-eon-bg border border-eon-border px-3 py-2 text-sm">
              {["available", "reserved", "sold", "coming_soon"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            <button onClick={() => delDog(d.id)} data-testid={`dog-delete-${d.id}`} className="text-eon-muted hover:text-red-400 p-2"><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
    </TabsContent>
  );
}

function ProductsTab({ products, delProduct, loadAll }) {
  return (
    <TabsContent value="products" className="pt-8">
      <div className="flex justify-end mb-4"><NewProductDialog onDone={loadAll} /></div>
      <div className="space-y-3">
        {products.map((p) => (
          <div key={p.id} className="eon-card p-4 flex items-center gap-4" data-testid={`admin-product-${p.id}`}>
            <img src={p.image} alt="" className="w-14 h-14 object-cover" />
            <div className="flex-1"><div className="font-display font-bold">{p.name}</div><div className="text-xs text-eon-muted">{p.category} · ${p.price?.toFixed(2)} · stock {p.stock}</div></div>
            <button onClick={() => delProduct(p.id)} data-testid={`product-delete-${p.id}`} className="text-eon-muted hover:text-red-400 p-2"><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
    </TabsContent>
  );
}

function UsersTab({ users, updateUser }) {
  return (
    <TabsContent value="users" className="pt-8 space-y-2">
      {users.map((u) => (
        <div key={u.id} className="eon-card p-4 flex items-center gap-4" data-testid={`admin-user-${u.id}`}>
          <div className="flex-1"><div className="font-display font-bold">{u.name}</div><div className="text-xs text-eon-muted">{u.email}</div></div>
          <select value={u.role} onChange={(e) => updateUser(u.id, { role: e.target.value })} className="bg-eon-bg border border-eon-border px-3 py-2 text-sm">
            {["buyer", "breeder", "business", "vendor", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={() => updateUser(u.id, { suspended: !u.suspended })} data-testid={`user-suspend-${u.id}`}
            className={`text-xs font-bold uppercase tracking-wider px-4 py-2 ${u.suspended ? "bg-eon-gold text-white" : "border border-eon-border text-eon-muted"}`}>{u.suspended ? "Unsuspend" : "Suspend"}</button>
        </div>
      ))}
    </TabsContent>
  );
}

function ServicesTab({ services, updateService, delService }) {
  return (
    <TabsContent value="services" className="pt-8 space-y-3">
      <p className="text-sm text-eon-muted mb-2">Marketplace listings submitted by members. Approve to make them visible.</p>
      {services.map((s) => (
        <div key={s.id} className="eon-card p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-service-${s.id}`}>
          <div className="flex-1">
            <div className="font-display font-bold text-eon-text">{s.name} <span className="text-[10px] font-bold uppercase tracking-widest text-eon-gold">{s.service_type}</span>{s.verified && <span className="ml-2 text-[10px] font-bold uppercase tracking-widest bg-eon-gold text-white rounded-full px-2 py-0.5">Verified</span>}{!s.verified && s.verification_requested && <span className="ml-2 text-[10px] font-bold uppercase tracking-widest bg-yellow-100 text-yellow-700 rounded-full px-2 py-0.5">Verify requested</span>}</div>
            <div className="text-xs text-eon-muted mt-1">{s.category} · {s.location || "—"} · {s.approved ? "Approved" : "Pending"}</div>
            {s.description && <p className="text-xs text-eon-text/70 mt-1">{s.description}</p>}
          </div>
          <button onClick={() => updateService(s.id, { verified: !s.verified })} data-testid={`service-verify-${s.id}`}
            className={`text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 ${s.verified ? "border border-eon-border text-eon-muted" : "border border-eon-gold text-eon-gold"}`}>{s.verified ? "Unverify" : "Verify"}</button>
          <button onClick={() => updateService(s.id, { approved: !s.approved })} data-testid={`service-approve-${s.id}`}
            className={`text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 ${s.approved ? "border border-eon-border text-eon-muted" : "bg-eon-gold text-white"}`}>{s.approved ? "Unpublish" : "Approve"}</button>
          <button onClick={() => delService(s.id)} data-testid={`service-delete-${s.id}`} className="text-eon-muted hover:text-red-500 p-2"><Trash2 size={18} /></button>
        </div>
      ))}
      {services.length === 0 && <EmptyRow text="No marketplace listings yet." />}
    </TabsContent>
  );
}

function InquiriesTab({ inquiries }) {
  return (
    <TabsContent value="inquiries" className="pt-8 space-y-3">
      {inquiries.map((q) => (
        <div key={q.id} className="eon-card p-5" data-testid={`admin-inquiry-${q.id}`}>
          <div className="flex justify-between"><div className="eon-label">{q.subject_type.replace("_", " ")}</div><span className="text-xs text-eon-muted">{q.status}</span></div>
          <div className="text-sm mt-2">{q.name} · {q.email} {q.phone && `· ${q.phone}`}</div>
          <p className="text-sm text-eon-text/70 mt-2">{q.message}</p>
        </div>
      ))}
      {inquiries.length === 0 && <EmptyRow text="No inquiries yet." />}
    </TabsContent>
  );
}

function AdminStats({ stats }) {
  const STATS = [
    { icon: Users, label: "Users", val: stats.users }, { icon: Dog, label: "Dogs", val: stats.dogs },
    { icon: Building2, label: "Breeders", val: stats.breeders }, { icon: Package, label: "Products", val: stats.products },
    { icon: ClipboardList, label: "Applications", val: stats.applications }, { icon: DollarSign, label: "Revenue", val: `$${stats.revenue ?? 0}` },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-eon-border border border-eon-border mb-10">
      {STATS.map((s) => (
        <div key={s.label} className="bg-eon-bg p-5" data-testid={`admin-stat-${s.label.toLowerCase()}`}>
          <s.icon size={18} className="text-eon-gold" /><div className="font-display text-2xl font-black mt-2">{s.val ?? 0}</div><div className="eon-label mt-1">{s.label}</div>
          {(s.label === "Breeders" && stats.pending_breeders > 0) && <div className="text-[10px] text-yellow-400 mt-1">{stats.pending_breeders} pending</div>}
          {(s.label === "Dogs" && stats.pending_dogs > 0) && <div className="text-[10px] text-yellow-400 mt-1">{stats.pending_dogs} pending</div>}
          {(s.label === "Applications" && stats.pending_applications > 0) && <div className="text-[10px] text-yellow-400 mt-1">{stats.pending_applications} pending</div>}
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [breeders, setBreeders] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [notify, setNotify] = useState([]);
  const [services, setServices] = useState([]);
  const [waitlists, setWaitlists] = useState([]);
  const [promos, setPromos] = useState([]);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/breeders").then((r) => setBreeders(r.data));
    api.get("/admin/dogs").then((r) => setDogs(r.data));
    api.get("/admin/products").then((r) => setProducts(r.data));
    api.get("/admin/inquiries").then((r) => setInquiries(r.data));
    api.get("/admin/applications").then((r) => setApplications(r.data));
    api.get("/admin/notify").then((r) => setNotify(r.data));
    api.get("/admin/services").then((r) => setServices(r.data));
    api.get("/admin/waitlists").then((r) => setWaitlists(r.data));
    api.get("/admin/promo-codes").then((r) => setPromos(r.data));
  }, []);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/login");
    if (user?.role === "admin") loadAll();
  }, [user, loading, nav, loadAll]);

  if (loading || !user) return <Layout><div className="max-w-[1400px] mx-auto px-5 py-32 text-eon-muted">Loading...</div></Layout>;

  const updateUser = async (id, body) => { await api.patch(`/admin/users/${id}`, body); toast.success("Updated"); loadAll(); };
  const updateBreeder = async (id, body) => { await api.patch(`/admin/breeders/${id}`, body); toast.success("Updated"); loadAll(); };
  const updateDog = async (id, body) => { await api.patch(`/admin/dogs/${id}`, body); toast.success("Updated"); loadAll(); };
  const delDog = async (id) => { await api.delete(`/admin/dogs/${id}`); toast.success("Deleted"); loadAll(); };
  const delProduct = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); loadAll(); };
  const updateApp = async (id, body) => { await api.patch(`/admin/applications/${id}`, body); toast.success("Updated"); loadAll(); };
  const updateService = async (id, body) => { await api.patch(`/admin/services/${id}`, body); toast.success("Updated"); loadAll(); };
  const delService = async (id) => { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); loadAll(); };
  const sendNotify = async (kind, item_id) => {
    const { data } = await api.post("/admin/notify/send", { kind, item_id });
    toast.success(data.sent > 0 ? `Sent ${data.sent} alert${data.sent === 1 ? "" : "s"}` : "No pending subscribers");
    loadAll();
  };
  const sendBlast = async (body) => {
    const { data } = await api.post("/admin/waitlists/blast", body);
    if (data.total === 0) toast.error("No one on this list yet");
    else if (data.sent > 0) toast.success(`Sent to ${data.sent} of ${data.total}`);
    else toast.success(`Queued ${data.total} — emails send once your Resend key is added`);
    loadAll();
  };
  const createPromo = async (body) => {
    try { await api.post("/admin/promo-codes", body); toast.success("Promo code created"); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not create code"); }
  };
  const togglePromo = async (id, active) => { await api.patch(`/admin/promo-codes/${id}`, { active }); toast.success("Updated"); loadAll(); };
  const delPromo = async (id) => { await api.delete(`/admin/promo-codes/${id}`); toast.success("Deleted"); loadAll(); };

  return (
    <Layout>
      <PageHeader label="Command Center" title="Admin Dashboard" subtitle="Manage users, listings, breeders, products and moderation." />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
        <AdminStats stats={stats} />

        <Tabs defaultValue="applications">
          <TabsList className="bg-transparent border-b border-eon-border rounded-none w-full justify-start h-auto p-0 gap-6 overflow-x-auto">
            {["applications", "notify", "waitlists", "promos", "breeders", "dogs", "products", "services", "users", "inquiries"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`}
                className="capitalize bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-eon-gold data-[state=active]:border-b-2 data-[state=active]:border-eon-gold rounded-none px-0 pb-3 text-eon-muted whitespace-nowrap">{t === "notify" ? "Waitlist Alerts" : t === "waitlists" ? "Waitlists" : t === "promos" ? "Promo Codes" : t === "services" ? "Marketplace" : t}</TabsTrigger>
            ))}
          </TabsList>

          <ApplicationsTab applications={applications} updateApp={updateApp} />
          <NotifyTab notify={notify} sendNotify={sendNotify} />
          <WaitlistsTab waitlists={waitlists} promos={promos} onSend={sendBlast} />
          <PromoCodesTab promos={promos} onCreate={createPromo} onToggle={togglePromo} onDelete={delPromo} />
          <BreedersTab breeders={breeders} updateBreeder={updateBreeder} />
          <DogsTab dogs={dogs} updateDog={updateDog} delDog={delDog} loadAll={loadAll} />
          <ProductsTab products={products} delProduct={delProduct} loadAll={loadAll} />
          <ServicesTab services={services} updateService={updateService} delService={delService} />
          <UsersTab users={users} updateUser={updateUser} />
          <InquiriesTab inquiries={inquiries} />
        </Tabs>
      </div>
    </Layout>
  );
}

function NewDogDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", breed: "Boerboel", sex: "male", price: 3000, state: "TX", city: "Dallas", color: "Fawn", category: "puppy", work_type: "companion", registration: "SABBS", age: "9 weeks", is_boerboel: false, image: "https://images.unsplash.com/photo-1594738985551-b2e28db213a0", description: "", breeder_id: "breeder_eon" });
  const save = async () => {
    try { await api.post("/admin/dogs", { ...f, price: Number(f.price), images: [f.image] }); toast.success("Dog added"); setOpen(false); onDone(); }
    catch { toast.error("Could not add dog"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="admin-new-dog-btn" className="flex items-center gap-2 bg-eon-gold text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 hover:bg-eon-goldhover transition-colors"><Plus size={16} /> New Dog</button></DialogTrigger>
      <DialogContent className="bg-eon-surface border-eon-border text-eon-text max-h-[85vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add Dog Listing</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={IN} data-testid="new-dog-name" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Breed" value={f.breed} onChange={(e) => setF({ ...f, breed: e.target.value })} className={IN} />
            <input type="number" placeholder="Price" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={IN} data-testid="new-dog-price" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={f.sex} onChange={(e) => setF({ ...f, sex: e.target.value })} className={IN}><option value="male">Male</option><option value="female">Female</option></select>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={IN}><option value="puppy">Puppy</option><option value="adult">Adult</option></select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={IN} />
            <input placeholder="State" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className={IN} />
          </div>
          <input placeholder="Image URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className={IN} />
          <textarea placeholder="Description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={IN} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_boerboel} onChange={(e) => setF({ ...f, is_boerboel: e.target.checked })} /> E.O.N Boerboel</label>
          <button onClick={save} className="w-full bg-eon-gold text-white font-bold uppercase tracking-wider py-3 hover:bg-eon-goldhover transition-colors" data-testid="new-dog-save">Save Listing</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewProductDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", category: "Collars", price: 49, stock: 25, image: "https://images.unsplash.com/photo-1546687813-3fcc1c363a07", description: "", brand: "E.O.N", is_eon: true, rating: 4.8, review_count: 10 });
  const save = async () => {
    try { await api.post("/admin/products", { ...f, price: Number(f.price), stock: Number(f.stock), images: [f.image] }); toast.success("Product added"); setOpen(false); onDone(); }
    catch { toast.error("Could not add product"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="admin-new-product-btn" className="flex items-center gap-2 bg-eon-gold text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 hover:bg-eon-goldhover transition-colors"><Plus size={16} /> New Product</button></DialogTrigger>
      <DialogContent className="bg-eon-surface border-eon-border text-eon-text max-h-[85vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add Product</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={IN} data-testid="new-product-name" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={IN} />
            <input type="number" placeholder="Price" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={IN} data-testid="new-product-price" />
          </div>
          <input type="number" placeholder="Stock" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} className={IN} />
          <input placeholder="Image URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className={IN} />
          <textarea placeholder="Description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={IN} />
          <button onClick={save} className="w-full bg-eon-gold text-white font-bold uppercase tracking-wider py-3 hover:bg-eon-goldhover transition-colors" data-testid="new-product-save">Save Product</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
